import { EcommerceProduct, OrderConfirmationEvent } from "./GoogleTagManagerIntegration";
import { Pushable, pushableEvent, pushableEventOfPure } from "./pushable";
import { getEcommerceProductVariant, toMoneyWithDecimals } from "./shared-utils";
import { pipe } from "fp-ts/lib/function";
import { option } from "fp-ts";
import {
  EnrichedSwarovskiProductVariantFrontend,
  SwarovskiOptikClientCart,
  SwarovskiOptikLocaleFrontend,
} from "@swo/storefront";
import { SwarovskiOptikSegmentShort } from "@swo/shared";
import { LocaleSegmentContext } from "../../global/page-context";
import { PDPSwarovskiProductVariantFrontend } from "../../layouts/product-detail-page-layout/PDP_SingleImageCarousel/state/types";

export const ecommerceTrackingAspect = {
  orderConfirmationEvent: (
    cart: SwarovskiOptikClientCart,
    orderId: string,
    segment: { id: string; key: string },
  ): Pushable => {
    const actionField: OrderConfirmationEvent["ecommerce"]["purchase"]["actionField"] | undefined = cart.taxedPrice
      ? {
          id: orderId,
          affiliation: "Online Store",
          revenue: toMoneyWithDecimals(cart.taxedPrice.totalGross),
          tax: cart.taxedPrice.taxPortions.map(p => p.amount).reduce((l, r) => l + toMoneyWithDecimals(r), 0),
          shipping: cart.shippingInfo ? toMoneyWithDecimals(cart.shippingInfo.price) : -1,
          // coupon: undefined,
        }
      : undefined;

    const productsPromise = Promise.all(cart.lineItems.map(li => getEcommerceProductVariant(li, segment))).then(_ =>
      _.flat(),
    );

    return actionField
      ? pushableEvent(
          productsPromise.then(products => ({
            event: "order-confirmation",
            ecommerce: {
              currencyCode: cart.totalPrice.currencyCode.toUpperCase(),
              purchase: {
                actionField: actionField,
                products: products,
              },
            },
          })),
        )
      : {
          push: () => {
            console.log(`Order confirmation event not valid.`);
          },
        };
  },
  checkoutSteps: (
    cartLineItems: SwarovskiOptikClientCart["lineItems"],
    locale: SwarovskiOptikLocaleFrontend,
    step: "view-cart" | "check-order",
    segment: { id: string; key: string },
  ): Pushable => {
    const productsPromise = Promise.all(cartLineItems.map(li => getEcommerceProductVariant(li, segment))).then(_ =>
      _.flat(),
    );

    return pushableEvent(
      productsPromise.then(products => ({
        event: "checkout-steps",
        ecommerce: {
          currencyCode: locale.currency.toUpperCase(),
          checkout: {
            actionField: { step: step === "view-cart" ? 1 : 2 },
            products: products,
          },
        },
      })),
    );
  },
  addToCart: (newElements: SwarovskiOptikClientCart["lineItems"], pageCtx: LocaleSegmentContext): Pushable => {
    const productsPromise = Promise.all(newElements.map(li => getEcommerceProductVariant(li, pageCtx.segment))).then(
      _ => _.flat(),
    );

    return pushableEvent(
      productsPromise.then(products => ({
        event: "add-to-cart",
        ecommerce: {
          currencyCode: pageCtx.locale.currency,
          add: {
            products: products.map(p => ({ ...p, quantity: 1 })),
          },
        },
      })),
    );
  },
  removeFromCart: (removedElements: SwarovskiOptikClientCart["lineItems"], pageCtx: LocaleSegmentContext): Pushable => {
    const productsPromise = Promise.all(
      removedElements.map(li => getEcommerceProductVariant(li, pageCtx.segment)),
    ).then(_ => _.flat());

    return pushableEvent(
      productsPromise.then(products => ({
        event: "remove-from-cart",
        ecommerce: {
          currencyCode: pageCtx.locale.currency,
          remove: {
            products: products.map(p => ({ ...p, quantity: 1 })),
          },
        },
      })),
    );
  },
  productDetailView: (
    productName: string,
    productVariant: PDPSwarovskiProductVariantFrontend,
    productCategoryKey: string,
    locale: SwarovskiOptikLocaleFrontend,
    segment: { id: string; key: string },
  ): Pushable => {
    return pushableEventOfPure({
      event: "product-detail" as const,
      ecommerce: {
        currencyCode: (productVariant.price && productVariant.price.currencyCode) || "-",
        detail: {
          products: [
            {
              name: productName,
              id: productVariant.sku,
              price: productVariant.price ? toMoneyWithDecimals(productVariant.price) : -1,
              category: productCategoryKey,
              quantity: 1,
              dimension10: segment.key,
              dimension7: productVariant.availability?.isOnStock ? "in-stock" : "out-of-stock",
            },
          ],
        },
      },
    });
  },
  productClick: (data: {
    productName: string;
    currencyCode: string;
    category: string;
    segmentKey: string;
  }): Pushable => {
    return pushableEventOfPure({
      event: "product-click",
      ecommerce: {
        currencyCode: data.currencyCode,
        click: {
          products: [
            {
              name: data.productName,
              category: data.category,
              dimension10: data.segmentKey,
            },
          ],
        },
      },
    });
  },
  productImpressions: (currencyCode: string, products: Array<EcommerceProduct>): Pushable => {
    return pushableEventOfPure({
      event: "product-impressions",
      ecommerce: {
        currencyCode: currencyCode,
        impressions: products.map((product, idx) => ({ ...product, position: idx + 1 })),
      },
    });
  },

  productAvailabilitySubscription: (
    localeCode: string,
    language: string,
    country: string,
    segmentKey: string,
    email: string,
    sku: string,
  ): Pushable => {
    return pushableEventOfPure({
      event: "product-availability-subscription",
      localeCode,
      language,
      country,
      segmentKey,
      email,
      sku,
    });
  },
};
